/* RIPOST cart CRO v1 (cart drawer): undo after removal + in-drawer variant change. See assets/CUSTOM.txt */
import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

const UNDO_MS = 6000;
const SOURCE_UNDO = 'ripost-undo';
const SOURCE_SWAP = 'ripost-variant-swap';

let pending = null;
let pendingAt = 0;
let toast = null;
let hideTimer = null;
let lastRemoved = null;

const getDrawer = () => document.querySelector('cart-drawer');
const sectionsParam = () => {
  const d = getDrawer();
  return d ? [d.dataset.sectionId, 'auto-gifts'].join(',') : 'auto-gifts';
};

function lineData(item) {
  if (!item) return null;
  let properties = {};
  try {
    properties = JSON.parse(item.dataset.properties || '{}') || {};
    /* Liquid serialises empty properties as [] (or as [key, value] pairs): the AJAX API wants an object */
    if (Array.isArray(properties)) properties = Object.fromEntries(properties.filter((p) => Array.isArray(p) && p.length === 2));
  } catch (e) {
    properties = {};
  }
  const line = {
    id: Number(item.dataset.variantId),
    quantity: Number(item.dataset.quantity) || 1,
    properties,
    title: item.dataset.productTitle || '',
  };
  if (item.dataset.sellingPlan) line.selling_plan = Number(item.dataset.sellingPlan);
  return line.id ? line : null;
}

function capture(item) {
  pending = lineData(item);
  pendingAt = Date.now();
}

/* Removal intent: trash button, or quantity brought to 0 */
document.addEventListener(
  'click',
  (event) => {
    const btn = event.target.closest('cart-drawer [data-ref="remove-item"]');
    if (btn) capture(btn.closest('cart-drawer-item'));
  },
  true,
);
document.addEventListener(
  'change',
  (event) => {
    const input = event.target.closest('cart-drawer [data-ref="quantity-selector-input"]');
    if (input && Number(input.value) <= 0) capture(input.closest('cart-drawer-item'));
  },
  true,
);

function ensureToast() {
  const drawer = getDrawer();
  if (!drawer) return null;
  if (toast && toast.isConnected) return toast;
  toast = document.createElement('div');
  /* dark theme color scheme (scheme-3: #2a2a2a / white), so the toast stays native to the palette */
  toast.className = 'ripost-cart-undo color-scheme-3';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.innerHTML =
    '<div class="ripost-cart-undo__text"><strong>Article retiré</strong><small data-ref="undo-title"></small></div>' +
    '<button type="button" class="ripost-cart-undo__button" data-ref="undo-button">Annuler</button>';
  toast.querySelector('[data-ref="undo-button"]').addEventListener('click', undo);
  /* Child of <cart-drawer> (not of the re-rendered inner panel), fixed-positioned over the panel */
  drawer.appendChild(toast);
  return toast;
}

function placeToast() {
  const drawer = getDrawer();
  if (!toast || !drawer) return;
  const inner = drawer.querySelector('.cart-drawer__inner');
  const footer = drawer.querySelector('.cart-drawer__footer');
  if (!inner) return;
  const r = inner.getBoundingClientRect();
  const inset = 16;
  const bottomEdge = footer && footer.offsetParent ? footer.getBoundingClientRect().top : r.bottom;
  toast.style.left = `${r.left + inset}px`;
  toast.style.width = `${Math.max(0, r.width - inset * 2)}px`;
  toast.style.top = 'auto';
  toast.style.bottom = `${Math.max(0, window.innerHeight - bottomEdge + 12)}px`;
}

function showToast(line) {
  const t = ensureToast();
  if (!t) return;
  lastRemoved = line;
  t.querySelector('[data-ref="undo-title"]').textContent = line.title;
  const btn = t.querySelector('[data-ref="undo-button"]');
  btn.disabled = false;
  placeToast();
  /* the drawer re-renders asynchronously after cart:updated: re-place once it settled */
  requestAnimationFrame(() => requestAnimationFrame(placeToast));
  setTimeout(placeToast, 250);
  t.classList.add('is-visible');
  clearTimeout(hideTimer);
  hideTimer = setTimeout(hideToast, UNDO_MS);
}

function hideToast() {
  clearTimeout(hideTimer);
  lastRemoved = null;
  if (toast) toast.classList.remove('is-visible');
}

function dispatchCart(data, itemCount, source) {
  document.dispatchEvent(new CartUpdatedEvent({}, { itemCount, source, sections: data.sections }));
}

async function fetchCount() {
  try {
    const cart = await fetch(`${(window.Theme && Theme.routes && Theme.routes.cart_url) || "/cart"}.js`).then((r) => r.json());
    return cart.item_count;
  } catch (e) {
    return undefined;
  }
}

async function undo() {
  if (!lastRemoved) return;
  const line = lastRemoved;
  const btn = toast.querySelector('[data-ref="undo-button"]');
  btn.disabled = true;
  const item = { id: line.id, quantity: line.quantity, properties: line.properties };
  if (line.selling_plan) item.selling_plan = line.selling_plan;
  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections: sectionsParam(), sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'add failed');
    hideToast();
    dispatchCart(data, await fetchCount(), SOURCE_UNDO);
  } catch (e) {
    console.error(e);
    btn.disabled = false;
    document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: e.message } }));
  }
}

document.addEventListener(ThemeEvents.cartUpdated, (event) => {
  const source = event.detail?.data?.source;
  if (source === 'cart-drawer-item' && pending && Date.now() - pendingAt < 15000) {
    const line = pending;
    pending = null;
    showToast(line);
    return;
  }
  pending = null;
  if (source !== 'cart-drawer-item') hideToast();
});

document.addEventListener(ThemeEvents.cartClose, hideToast);

// QA 24/10/2026: Esc closes the open cart drawer (empty or not), unless a dialog (quick add, lightbox…) is open on top.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || event.defaultPrevented) return;
  const drawer = document.querySelector('cart-drawer.is-open');
  if (!drawer || typeof drawer.close !== 'function' || document.querySelector('dialog[open]')) return;
  drawer.close();
});
window.addEventListener('resize', placeToast);

/* "Modifier": reveal the variant select, then swap the line for the chosen variant */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('cart-drawer [data-ref="ripost-variant-edit"]');
  if (!btn) return;
  event.preventDefault();
  const item = btn.closest('cart-drawer-item');
  const box = item && item.querySelector('.ripost-cart-variant');
  if (!box) return;
  const open = box.hidden;
  box.hidden = !open;
  btn.setAttribute('aria-expanded', String(open));
  if (open) box.querySelector('select')?.focus();
});

document.addEventListener('change', async (event) => {
  const select = event.target.closest('cart-drawer [data-ref="ripost-variant-select"]');
  if (!select) return;
  const item = select.closest('cart-drawer-item');
  const line = lineData(item);
  const newId = Number(select.value);
  if (!line || !newId || newId === line.id) return;
  item.classList.add('cart-drawer__item--loading');
  select.disabled = true;
  try {
    const removeRes = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.dataset.lineKey, quantity: 0 }),
    });
    if (!removeRes.ok) throw new Error('change failed');
    const add = { id: newId, quantity: line.quantity, properties: line.properties };
    if (line.selling_plan) add.selling_plan = line.selling_plan;
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [add], sections: sectionsParam(), sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      /* new variant refused (stock): put the original line back */
      const back = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: line.id, quantity: line.quantity, properties: line.properties }], sections: sectionsParam() }),
      }).then((r) => r.json());
      dispatchCart(back, await fetchCount(), SOURCE_SWAP);
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: data.description || data.message || 'Option indisponible' } }));
      return;
    }
    dispatchCart(data, await fetchCount(), SOURCE_SWAP);
  } catch (e) {
    console.error(e);
    item.classList.remove('cart-drawer__item--loading');
    select.disabled = false;
  }
});
